import React from 'react';
import { ArrowRight, Store, TrendingDown, Timer, DollarSign, AlertCircle, Package, Users } from 'lucide-react';

// 10 Procurement Metrics - Total Buah Segar Pilot Data
const kpis = [
  { icon: AlertCircle,  value: '$12.4K', label: 'Claims recovered this month', tone: 'good', category: 'Money' },
  { icon: Package,      value: '$5K',    label: 'Dead stock prevented', tone: 'good', category: 'Money' },
  { icon: DollarSign,   value: '$8.3K',  label: 'Price optimization savings', tone: 'good', category: 'Money' },
  { icon: TrendingDown, value: '$3K',    label: 'Expedited shipping avoided', tone: 'good', category: 'Money' },
  { icon: DollarSign,   value: '$1.5K',  label: 'Volume discounts captured', tone: 'good', category: 'Money' },
  { icon: Timer,        value: '56 hrs', label: 'PO chasing (per week) freed', tone: 'good', category: 'Labor' },
  { icon: Users,        value: '2.3 FTE',label: 'Procurement team freed', tone: 'good', category: 'Labor' },
  { icon: TrendingDown, value: '−38%',   label: 'Produce waste, week over week', tone: 'good', category: 'Waste' },
  { icon: DollarSign,   value: '+11%',   label: 'Basket revenue on pilot stores', tone: 'good', category: 'Revenue' },
  { icon: Store,        value: '6',      label: 'Branches live with full automation', tone: 'brand', category: 'Scale' },
];

const timeline = [
  { t: 'Week 0', title: 'Kickoff · discovery', body: 'Mapped POS, supplier feeds, and cold-chain SOPs across the fleet.' },
  { t: 'Week 2', title: 'Store 04 goes live', body: 'First pilot store trained on 18 months of basket history + weather.' },
  { t: 'Week 6', title: 'Six branches live', body: 'Ordering and prep run from Grocera; managers approve on phone.' },
  { t: 'Week 12', title: '6 branches live', body: 'Full pilot rollout; weekly impact report delivered every Monday.' },
];

const quote = {
  text:
    'Grocera moved from insight to action. Our produce shrink is down, our shelves stay full through the evening rush, and my managers spend less time on spreadsheets.',
  who: 'Operations Lead',
  brand: 'Total Buah Segar',
};

export default function CaseStudy() {
  return (
    <section id="case-study" className="section relative text-white border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
          <div className="lg:col-span-12">
            <p className="text-white/60 text-[15px] leading-[1.7] max-w-[720px]">
              Indonesia&rsquo;s Total Buah Segar chain runs its produce forecast, ordering, cold
              chain and staffing on Grocera. We started with one store and rolled out to six
              over twelve weeks. A central warehouse now auto-replenishes every branch every
              morning, so shelves stay stocked without a manager placing an order.
            </p>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {kpis.map((k) => {
            const Icon = k.icon;
            const color = k.tone === 'brand' ? '#10b981' : '#2E6BFF';
            return (
              <div key={k.label} className="card-line p-5">
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center mb-4"
                  style={{ background: `${color}22`, border: `1px solid ${color}55` }}
                >
                  <Icon size={16} style={{ color }} />
                </div>
                <div className="h-display text-[36px]" style={{ color }}>
                  {k.value}
                </div>
                <div className="mono text-[10.5px] text-white/55 uppercase tracking-wider mt-2">
                  {k.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Rollout timeline</p>
            <div className="relative pl-10">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-white/10" />
              {timeline.map((s, i) => (
                <div key={s.t} className="relative pb-8 last:pb-0">
                  <div
                    className="absolute -left-10 top-0 w-6 h-6 flex items-center justify-center rounded-sm mono text-[10px]"
                    style={{
                      background: i < timeline.length ? '#10b981' : 'rgba(5,5,6,0.9)',
                      color: '#052e19',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="mono text-[10.5px] text-white/45 uppercase tracking-widest mb-1">
                    {s.t}
                  </div>
                  <h3 className="h-sans text-white text-[19px] mb-1.5">{s.title}</h3>
                  <p className="text-white/60 text-[14px] leading-[1.65] max-w-[520px]">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote card */}
          <div className="lg:col-span-5">
            <div className="card-line p-6 h-full flex flex-col justify-between">
              <div>
                <div className="h-display text-[36px] leading-none text-[#2E6BFF] mb-4">&ldquo;</div>
                <p className="text-white text-[18px] leading-[1.55] mb-6">{quote.text}</p>
              </div>
              <div>
                <div className="h-sans text-white text-[15px]">{quote.who}</div>
                <div className="mono text-[11px] text-white/50 uppercase tracking-widest mt-1">
                  {quote.brand}
                </div>
              </div>
            </div>

            <a
              href="https://calendar.app.google/rUCGByUhUwE69b2T9"
              target="_blank"
              rel="noreferrer"
              className="btn-primary mt-6 w-full justify-center"
            >
              See it live in your stores <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
